import { User } from "oidc-client-ts";
import { doGet } from "./util/apiHelper.ts";
import { useAuthenticatedInfiniteQuery } from "./util/queryHelper.ts";
import { APPOINTMENT_QUERY_KEYS } from "./util/queryKeys.ts";

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

export type AppointmentSource = "BOOKING" | "SUBSCRIPTION";

export interface AppointmentDto {
  source: AppointmentSource;
  from: string; // ISO-8601 instant
  to: string; // ISO-8601 instant
  instrument: string;
  locationName: string;
  personId: string;
  personName: string;
  personProfilePictureUrl?: string;
  bookingId?: string;
  subscriptionId?: string;
}

export interface AppointmentsPageDto {
  appointments: AppointmentDto[];
  total: number;
  page: number;
  hasMore: boolean;
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

const PAGE_SIZE = 10;

async function getScholarAppointments(
  user: User,
  page: number,
): Promise<AppointmentsPageDto> {
  return await doGet<AppointmentsPageDto>(
    `/users/${user.profile.sub}/appointments`,
    user,
    { page, size: PAGE_SIZE },
  );
}

async function getTeacherAppointments(
  user: User,
  page: number,
): Promise<AppointmentsPageDto> {
  return await doGet<AppointmentsPageDto>(
    `/teachers/${user.profile.sub}/appointments`,
    user,
    { page, size: PAGE_SIZE },
  );
}

// -------------------------------------------------------------------------
// Hooks
// -------------------------------------------------------------------------

export function useScholarAppointmentsQuery() {
  return useAuthenticatedInfiniteQuery(
    APPOINTMENT_QUERY_KEYS.scholarAppointments(),
    (user, page) => getScholarAppointments(user, page),
    (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  );
}

export function useTeacherAppointmentsQuery() {
  return useAuthenticatedInfiniteQuery(
    APPOINTMENT_QUERY_KEYS.teacherAppointments(),
    (user, page) => getTeacherAppointments(user, page),
    (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  );
}
